import React from "react";
import { useLocation } from "react-router";

import useSWR from "swr";

import ListItem from "@mui/material/ListItem";

import { useFilter, useSort } from "@tzmedical/react-hooks";

import arrayIncludesAny from "../../../shared/arrayIncludesAny.js";
import { swrFetcher } from "../../axiosClient.js";
import Alert from "../../components/common/Alert.jsx";
import PageLoading from "../../components/common/PageLoading.jsx";
import EditButton from "../../components/EditButton.jsx";
import EngineeringProjectForm from "../../components/EngineeringProjectForm.jsx";
import { readableProjectName } from "../../components/ReadableProjectName.jsx";
import PageContext from "../../contexts/PageContext.jsx";
import SearchContext from "../../contexts/SearchContext.jsx";
import useAuth0Roles from "../../hooks/auth0Roles.jsx";
import EngineeringProjectRow from "./EngineeringProjectRow.jsx";
import EngineeringProjectsHeader from "./EngineeringProjectsHeader.jsx";

//-----------------------------------------------------------------------------
// Page configuration
//-----------------------------------------------------------------------------
const DATA_REFRESH_INTERVAL_MS = 15000;
const searchFields = {
  name: (project) => readableProjectName(project),
  gl: "gl",
  budget: "budget",
  cost: "cost",
  hours: "hours",
  startDate: "startDate",
  endDate: "endDate",
};
const sortOptions = {
  defaultSort: [
    {
      field: "name",
      reverse: false,
    },
  ],
};

function EngineeringProjectsPage() {
  const location = useLocation();

  const { roles } = useAuth0Roles();
  const canEdit = React.useMemo(
    () => arrayIncludesAny(roles, ["Scorecard Admin", "Scorecard PLC Admin"]),
    [roles]
  );

  //---------------------------------------------------------------------------
  // Fetch the data using SWR
  //---------------------------------------------------------------------------
  const {
    data: swrData,
    error: swrError,
    isLoading: swrLoading,
    mutate,
  } = useSWR({ url: "/api/engineering-projects" }, swrFetcher, {
    refreshInterval: DATA_REFRESH_INTERVAL_MS,
  });

  const error = swrError ? swrError.response?.data?.error || swrError.message : null;
  const setError = React.useCallback(
    (val) => {
      if (val === null) {
        mutate();
      }
    },
    [mutate]
  );

  const projectsList = React.useMemo(() => swrData || [], [swrData]);
  const tableLoading = swrLoading && !projectsList.length;

  //---------------------------------------------------------------------------
  // Navbar button
  //---------------------------------------------------------------------------
  const { setPageButtons } = React.useContext(PageContext);
  React.useEffect(() => {
    if (canEdit) {
      setPageButtons(
        <span>
          <ListItem>
            <EngineeringProjectForm mutate={mutate} />
          </ListItem>
          <ListItem sx={{ mb: 1 }}>
            <EditButton>Edit Data</EditButton>
          </ListItem>
        </span>
      );
    }
    return () => setPageButtons(null);
  }, [setPageButtons, canEdit, location, mutate]);

  //---------------------------------------------------------------------------
  // Search
  //---------------------------------------------------------------------------
  const { search, setSearch } = React.useContext(SearchContext);
  const filteredProjects = useFilter(projectsList, search, searchFields);
  const [sortedProjects, handleSortSelection, sort] = useSort(filteredProjects, sortOptions);
  React.useEffect(() => {
    return () => {
      // Reset the search bar every time we navigate to a new page
      setSearch("");
      mutate();
    };
  }, [setSearch, location, mutate]);

  //---------------------------------------------------------------------------
  //---------------------------------------------------------------------------
  if (tableLoading && !projectsList.length) {
    return (
      <>
        <Alert message={error} setMessage={setError} level="error" />
        {!error && <PageLoading />}
      </>
    );
  }

  //---------------------------------------------------------------------------
  // Render
  //---------------------------------------------------------------------------
  return (
    <>
      <Alert message={error} setMessage={setError} level="error" />
      <EngineeringProjectsHeader sort={sort} setSort={handleSortSelection} />
      {sortedProjects.map((project) => (
        <EngineeringProjectRow key={project.id} project={project} mutate={mutate} />
      ))}
    </>
  );
}

export default EngineeringProjectsPage;
